import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';

export default function Guidelines() {
  return (
    <>
      <Meta
        title="Guidelines for Developers"
        description="Get resources for being a paragon of virtue when developing for the Vimeo API."
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Guidelines for Developers</Path.Current>
          </Path>
        }
        nav={{ page: 'guidelines' }}
      >
        <Header>Guidelines for Developers</Header>
        <Paragraph size="2">
          Here are some guidelines to keep in mind when using the Vimeo API.
        </Paragraph>

        <Header size="2" id="terms">
          Terms of service
        </Header>
        <Paragraph size="2">
          Legal wants you to read our{' '}
          <Link variant="minimal" href="https://vimeo.com/legal/service-terms/api">
            Vimeo Developer Addendum
          </Link>
          . It spells out everything you need to know about what you can and can't do around here.
        </Paragraph>

        <Header size="2" id="rate-limiting">
          Rate limiting
        </Header>
        <Paragraph size="2">
          If your app makes too many API requests in too short of a time, we reserve the right to
          ignore you for a while like we would a bratty kid who keeps asking why. Learn exactly how
          much is too much, and gain valuable techniques for maximizing your quota, by reviewing our{' '}
          <Link variant="minimal" href="/guidelines/rate-limiting">
            Working with Rate Limits
          </Link>{' '}
          guide.
        </Paragraph>

        <Header size="2" id="best-practices">
          Best practices
        </Header>
        <Paragraph size="2">
          In addition to these rules of the road, we've found the following development strategies
          to be highly advisable.
        </Paragraph>

        <Header size="3" id="dont-reuse-client-ids">
          Don't reuse client identifiers
        </Header>
        <Paragraph size="2">
          You can register an unlimited number of apps, so get a new client identifier for each new
          project. See{' '}
          <Link variant="minimal" href="/api/guides/start#register-your-app">
            Getting Started: The Basics
          </Link>{' '}
          for step-by-step instructions.
        </Paragraph>

        <Header size="3" id="cache">
          Do cache
        </Header>
        <Paragraph size="2">
          API requests don't need to be made on every one of your user's requests. Any URLs that you
          get back from the API, including embeds, files, and image links, are guaranteed for at
          least one hour after the request is made, unless otherwise specified.
        </Paragraph>
        <Paragraph size="2">
          Caching the data that you get back from the API for up to an hour can improve the
          performance of your app and help the API to run more efficiently for everybody. Just
          remember to refresh your cache before using any of the embedded URLs by executing the
          request again and updating your cache accordingly.
        </Paragraph>
        <Paragraph size="2">
          This practice can also keep you right with your request quota. For more details, see{' '}
          <Link variant="minimal" href="/guidelines/rate-limiting#cache-api-responses">
            Working with Rate Limiting
          </Link>
          .
        </Paragraph>

        <Header size="3" id="encode-in-utf8">
          Do encode in UTF-8
        </Header>
        <Paragraph size="2">
          If you don't, you run the risk of errors. Our data back to you will always be in UTF-8.
          For deeper insight into our data representations, see{' '}
          <Link variant="minimal" href="/api/common-formats#working-with-representations">
            Using Common Formats and Parameters
          </Link>
          .
        </Paragraph>

        <Header size="3" id="custom-user-agents">
          Do define custom user agents
        </Header>
        <Paragraph size="2">
          We like to see custom user agents. It gives us reassurance that you're not doing something
          that the terms of service wouldn't approve of. Find out more about custom user agents in{' '}
          <Link variant="minimal" href="/api/common-formats#using-the-user-agent-header">
            Using Common Formats and Parameters
          </Link>
          .
        </Paragraph>

        <Header size="3" id="ask-for-help">
          When in doubt, ask
        </Header>
        <Paragraph size="2">
          You know how some people say that asking for forgiveness is always better than asking for
          permission? They clearly didn't read our terms of service. If you don't want to be
          deep-sixed for your edge case scenario, or if you're confused about something that we did
          or didn't tell you,{' '}
          <Link variant="minimal" href="http://vimeo.com/help/contact">
            contact us
          </Link>{' '}
          and see for yourself just how helpful, responsive, understanding, empowering, encouraging,
          knowledgeable, and friendly we are.
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
